import { priorityRank } from "./constants";

export function findNextAvailableSlot(tasks, start, end) {
  const duration = end - start;
  let slotStart = new Date(end);
  let slotEnd = new Date(slotStart.getTime() + duration);

  // ✅ Ensure all datetime fields are parsed with fallback in case they're null/undefined
  while (tasks.some(task => {
    try {
      const taskStart = task.datetime ? new Date(task.datetime) : null;
      const taskEnd = task.deadline ? new Date(task.deadline) : null;
      
      // Skip invalid dates
      if (!taskStart || !taskEnd || isNaN(taskStart.getTime()) || isNaN(taskEnd.getTime())) {
        return false;
      }
      
      return (slotStart >= taskStart && slotStart < taskEnd) ||
             (slotEnd > taskStart && slotEnd <= taskEnd);
    } catch (error) {
      console.warn('Invalid task datetime in findNextAvailableSlot:', task, error);
      return false;
    }
  })) {
    slotStart = new Date(slotEnd);
    slotEnd = new Date(slotStart.getTime() + duration);
  }
  return { slotStart, slotEnd };
}

export function findClashingTask(tasks, newStart, newEnd) {
  return tasks.find((task) => {
    try {
      // ✅ Wrap all date comparisons in try/catch and guard against invalid dates
      const taskStart = task.datetime ? new Date(task.datetime) : null;
      const taskEnd = task.deadline ? new Date(task.deadline) : null;
      
      // Skip tasks with invalid or missing dates
      if (!taskStart || !taskEnd || isNaN(taskStart.getTime()) || isNaN(taskEnd.getTime())) {
        return false;
      }
      
      return (
        (newStart >= taskStart && newStart < taskEnd) ||
        (newEnd > taskStart && newEnd <= taskEnd) ||
        (newStart <= taskStart && newEnd >= taskEnd)
      );
    } catch (error) {
      console.warn('Invalid task datetime in findClashingTask:', task, error);
      return false;
    }
  });
}

export function sortTasks(taskList, sortBy = "time") {
  if (sortBy === "time") {
    return [...taskList].sort((a, b) => {
      try {
        const aTime = a.datetime ? new Date(a.datetime) : null;
        const bTime = b.datetime ? new Date(b.datetime) : null;
        // Date-only tasks (no datetime) sort after all timed tasks
        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;
        if (isNaN(aTime.getTime()) || isNaN(bTime.getTime())) {
          if (isNaN(aTime.getTime()) && isNaN(bTime.getTime())) return 0;
          if (isNaN(aTime.getTime())) return 1;
          return -1;
        }
        return aTime - bTime;
      } catch (error) {
        console.warn('Error sorting tasks by time:', error);
        return 0;
      }
    });
  } else {
    return [...taskList].sort((a, b) => {
      try {
        // ✅ Add fallback to priorityRank lookup to avoid undefined values
        const rankA = priorityRank[a.priority] ?? 4;
        const rankB = priorityRank[b.priority] ?? 4;
        
        if (rankA !== rankB) return rankA - rankB;
        
        // ✅ Guard against invalid dates in fallback sort
        const aTime = a.datetime ? new Date(a.datetime) : new Date(0);
        const bTime = b.datetime ? new Date(b.datetime) : new Date(0);
        
        if (isNaN(aTime.getTime()) || isNaN(bTime.getTime())) {
          if (isNaN(aTime.getTime()) && isNaN(bTime.getTime())) return 0;
          if (isNaN(aTime.getTime())) return 1;
          if (isNaN(bTime.getTime())) return -1;
        }
        
        return aTime - bTime;
      } catch (error) {
        console.warn('Error sorting tasks by priority:', error);
        return 0;
      }
    });
  }
}

export function sortByTimeOnly(taskList) {
  return [...taskList].sort((a, b) => {
    try {
      const aTime = a.datetime ? new Date(a.datetime) : null;
      const bTime = b.datetime ? new Date(b.datetime) : null;
      // Date-only tasks sort after timed tasks
      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;
      if (isNaN(aTime.getTime()) || isNaN(bTime.getTime())) {
        if (isNaN(aTime.getTime()) && isNaN(bTime.getTime())) return 0;
        if (isNaN(aTime.getTime())) return 1;
        return -1;
      }
      return aTime - bTime;
    } catch (error) {
      console.warn('Error in sortByTimeOnly:', error);
      return 0;
    }
  });
}

export function sortTasksByUrgency(taskList) {
  const now = new Date();
  return [...taskList].sort((a, b) => {
    try {
      // ✅ Guard against invalid dates and wrap in try/catch
      const aTime = a.deadline ? new Date(a.deadline) : new Date(0);
      const bTime = b.deadline ? new Date(b.deadline) : new Date(0);

      // Handle invalid dates
      if (isNaN(aTime.getTime()) || isNaN(bTime.getTime())) {
        if (isNaN(aTime.getTime()) && isNaN(bTime.getTime())) return 0;
        if (isNaN(aTime.getTime())) return 1; // Put invalid dates at end
        if (isNaN(bTime.getTime())) return -1;
      }

      const isAOverdue = aTime < now;
      const isBOverdue = bTime < now;
      if (isAOverdue !== isBOverdue) return isAOverdue ? -1 : 1;

      // ✅ Add fallback to priorityRank lookup to avoid undefined values
      const aPriority = priorityRank[a.priority] ?? 4;
      const bPriority = priorityRank[b.priority] ?? 4;
      if (aPriority !== bPriority) return aPriority - bPriority;

      return aTime - bTime;
    } catch (error) {
      console.warn('Error in sortTasksByUrgency:', error);
      return 0;
    }
  });
}

export function tasksForDate(date, tasks) {
  return tasks.filter((task) => {
    try {
      if (task.completed) return false;
      if (!task.datetime) return false;
      const taskDate = new Date(task.datetime);
      if (isNaN(taskDate.getTime())) return false;
      return taskDate.toDateString() === date.toDateString();
    } catch (error) {
      console.warn('Error filtering tasks for date:', task, error);
      return false;
    }
  });
}

/**
 * Check if the clashing task has lower priority than the incoming task.
 * Returns a suggestion object if a swap would resolve the conflict cleanly.
 */
export function suggestPrioritySwap(clashTask, newTaskPriority) {
  if (!clashTask || !newTaskPriority) return null;
  const clashRank = priorityRank[clashTask.priority] || 2;
  const newRank = priorityRank[newTaskPriority] || 2;
  if (clashRank <= newRank) return null; // clashing task is equal or higher priority, no swap
  return {
    canSwap: true,
    clashTask,
    message: `"${clashTask.title}" is ${clashTask.priority} priority, lower than your new task. Move it to free up this slot?`,
  };
}

/**
 * Like findNextAvailableSlot but searches across multiple days if no same-day
 * slot is found within working hours (07:00–21:00).
 */
export function findNextAvailableSlotMultiDay(tasks, start, end, daysAhead = 3) {
  const duration = end - start;
  const workDayStart = 7;  // 07:00
  const workDayEnd   = 21; // 21:00

  for (let dayOffset = 0; dayOffset <= daysAhead; dayOffset++) {
    const baseDate = new Date(start);
    baseDate.setDate(baseDate.getDate() + dayOffset);

    // Start search from the original time on day 0, 07:00 on subsequent days
    let slotStart = new Date(baseDate);
    if (dayOffset > 0) slotStart.setHours(workDayStart, 0, 0, 0);
    let slotEnd = new Date(slotStart.getTime() + duration);

    const dayLimit = new Date(baseDate);
    dayLimit.setHours(workDayEnd, 0, 0, 0);

    while (slotEnd <= dayLimit) {
      const hasConflict = tasks.some(task => {
        try {
          const taskStart = task.datetime ? new Date(task.datetime) : null;
          const taskEnd   = task.deadline  ? new Date(task.deadline)  : null;
          if (!taskStart || !taskEnd || isNaN(taskStart) || isNaN(taskEnd)) return false;
          return (slotStart < taskEnd && slotEnd > taskStart);
        } catch { return false; }
      });

      if (!hasConflict) return { slotStart: new Date(slotStart), slotEnd: new Date(slotEnd), dayOffset };
      slotStart = new Date(slotEnd);
      slotEnd   = new Date(slotStart.getTime() + duration);
    }
  }
  return null; // No slot found within daysAhead
}